import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUserData } from '../../../../store/setting/actions';
import { apiHandler } from '../../../../api/apihandler';
import {
  LOGIN_WITH_EMAIL,
  LOGIN_WITH_EMAIL_OTP_MATCH,
  RESEND_LOGIN_OTP,
  LOGIN,
  LOGIN_OTP,
  RESEND_OTP,
} from '../../../../api/constants';
import { apiErrorMessage, apiSucceeded } from './apiUtils';
import ContactStep from './steps/ContactStep';
import VerifyStep from './steps/VerifyStep';
import FoodStoryStep from './steps/FoodStoryStep';
import ProfileStep from './steps/ProfileStep';
import FollowStep from './steps/FollowStep';
import WelcomeStep from './steps/WelcomeStep';
import './onboarding.css';

const RESEND_SECONDS = 60;

// NOTE: only the email path is backed by a confirmed API contract (see the
// EmailSignupScreen.js reference this was ported from). The phone path calls
// the symmetric LOGIN / LOGIN_OTP / RESEND_OTP endpoints with a best-guess
// payload shape ({ mobile, country_code }) since no reference implementation
// was available for phone signup — verify against the real backend before
// relying on it.

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [method, setMethod] = useState(searchParams.get('method') === 'phone' ? 'phone' : 'email');
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(false);

  const [contact, setContact] = useState({ email: '', phone: '', countryCode: '+1' });
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);
  const [contactError, setContactError] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    gender: null,
    aboutYou: '',
    coverPhoto: null,
    coverPhotoPreview: null,
    avatarPhoto: null,
    avatarPhotoPreview: null,
    dob: '',
    password: '',
    confirmPassword: '',
  });

  // Holds the auth token/user between OTP verification and the end of
  // onboarding without touching localStorage/Redux, so a user who abandons
  // onboarding mid-flow isn't left "logged in" with an incomplete profile —
  // mirrors the mobile app's EmailSignupScreen.js pendingTokenRef pattern.
  const pendingTokenRef = useRef(null);
  const pendingUserRef = useRef(null);

  useEffect(() => {
    if (step !== 2 || resendSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setResendSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, resendSeconds]);

  const identifier = method === 'email' ? contact.email.trim().toLowerCase() : contact.phone.replace(/\D/g, '');

  const handleToggleMethod = () => {
    setMethod((m) => (m === 'email' ? 'phone' : 'email'));
    setContactError('');
  };

  const handleSendCode = async () => {
    setSendingCode(true);
    setContactError('');

    const res = method === 'email'
      ? await apiHandler(LOGIN_WITH_EMAIL, 'POST', { email: identifier })
      : await apiHandler(LOGIN, 'POST', { mobile: identifier, country_code: contact.countryCode });

    setSendingCode(false);

    if (!apiSucceeded(res)) {
      setContactError(apiErrorMessage(res, 'Unable to send code.'));
      return;
    }

    setOtp(Array(6).fill(''));
    setResendSeconds(RESEND_SECONDS);
    setVerifyError('');
    setStep(2);
  };

  const handleResend = async () => {
    const res = method === 'email'
      ? await apiHandler(RESEND_LOGIN_OTP, 'POST', { email: identifier })
      : await apiHandler(RESEND_OTP, 'POST', { mobile: identifier });

    if (apiSucceeded(res)) {
      setOtp(Array(6).fill(''));
      setResendSeconds(RESEND_SECONDS);
      setVerifyError('');
    } else {
      setVerifyError(apiErrorMessage(res, 'Unable to resend code.'));
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyError('');

    const otpValue = otp.join('');
    const res = method === 'email'
      ? await apiHandler(LOGIN_WITH_EMAIL_OTP_MATCH, 'POST', { email: identifier, otp: otpValue })
      : await apiHandler(LOGIN_OTP, 'POST', { mobile: identifier, otp: otpValue });

    setVerifying(false);

    if (!apiSucceeded(res)) {
      setVerifyError(apiErrorMessage(res, 'OTP verification failed.'));
      return;
    }

    const data = res?.data?.data || null;
    const token = data?.token?.token || (typeof data?.token === 'string' ? data.token : null);
    const registerFirstTime = data?.registerFirstTime ?? data?.user?.registerFirstTime;

    if (registerFirstTime === false) {
      // Returning user — nothing left to onboard, log them straight in.
      if (token) localStorage.setItem('uzoneAuthToken', token);
      localStorage.setItem('user_email', identifier);
      if (token) dispatch(setUserData(JSON.stringify(data)));
      navigate('/pages/home');
      return;
    }

    pendingTokenRef.current = token;
    pendingUserRef.current = data;
    setStep(3);
  };

  const handleFoodStorySubmit = () => setStep(4);
  const handleProfileSubmit = () => setStep(5);

  const finishOnboarding = () => {
    // Safe to persist now — onboarding is fully complete.
    if (pendingTokenRef.current) {
      localStorage.setItem('uzoneAuthToken', pendingTokenRef.current);
    }
    localStorage.setItem('user_email', identifier);
    dispatch(setUserData(JSON.stringify({
      ...pendingUserRef.current,
      firstName: profile.firstName,
      lastName: profile.lastName,
      gender: profile.gender,
      aboutYou: profile.aboutYou,
      dateOfBirth: profile.dob,
    })));
    navigate('/pages/home');
  };

  const goBack = () => {
    if (step === 1) {
      navigate('/auth/sign-up');
      return;
    }
    setStep((s) => s - 1);
  };

  return (
    <div className="ob-root">
      {completed && <WelcomeStep fullName={`${profile.firstName} ${profile.lastName}`.trim()} onExplore={finishOnboarding} />}
      {!completed && step === 1 && (
        <ContactStep
          method={method}
          contact={contact}
          setContact={setContact}
          onToggleMethod={handleToggleMethod}
          onSubmit={handleSendCode}
          onBack={goBack}
          submitting={sendingCode}
          error={contactError}
        />
      )}
      {!completed && step === 2 && (
        <VerifyStep
          method={method}
          contact={contact}
          otp={otp}
          setOtp={setOtp}
          resendSeconds={resendSeconds}
          onResend={handleResend}
          onSubmit={handleVerify}
          onBack={goBack}
          error={verifyError}
          submitting={verifying}
        />
      )}
      {!completed && step === 3 && (
        <FoodStoryStep
          pendingToken={pendingTokenRef.current}
          onSubmit={handleFoodStorySubmit}
          onBack={goBack}
        />
      )}
      {!completed && step === 4 && (
        <ProfileStep
          profile={profile}
          setProfile={setProfile}
          pendingToken={pendingTokenRef.current}
          onSubmit={handleProfileSubmit}
          onBack={goBack}
        />
      )}
      {!completed && step === 5 && (
        <FollowStep
          pendingToken={pendingTokenRef.current}
          onSubmit={() => setCompleted(true)}
          onBack={goBack}
        />
      )}
    </div>
  );
};

export default OnboardingFlow;
